import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { WIDGET_SIDEBAR_POSITION_BOTTOM } from "../../constants/widget-types";
import { getStreamMeta } from "../../../../common/src/stream";
import { usePlayerContext } from "../../context/PlayerContext";
import { useEventContext } from "../../context/EventContext";
import { useCommentsContext } from "../../context/CommentsContext";
import { useQuestionsContext } from "../../context/QuestionsContext";
import { usePollsContext } from "../../context/PollsContext";
import { useWidgetContext } from "../../context/WidgetContextProvider";
import { overlays } from "../../components/Player/Overlay";
import { useStreamContext } from "../../context/StreamContext";
import { FULLSCREEN_MODES, RECORD_STATUSES, STREAM_STATUSES } from "../../constants/common";
import SideModule from "../../components/SideModule";
import ModuleMobileWidget from "../../components/SideModule/ModuleMobileWidget";
import ButtonQuestionBlock from "../ButtonQuestionBlock";
import { getEpisodeRecordStatus } from "../../helpers/stream-helper";

import "./style.scss";

export default ({ onHidePlayer, settings, isShowSideModuleByDefault = true }) => {
    const { event } = useEventContext();
    const { streams, stream: currentStream, setStream: changeStream } = useStreamContext();
    const {
        setEvent,
        setStream,
        stream,
        paused,
        mountPlayer,
        unmountPlayer,
        isFloating,
        isNotMountMiniPlayer,
        tabletView,
        isPlayerWidget,
        fullScreen,
        containerRef,
    } = usePlayerContext();
    const unmountPlayerRef = useRef(unmountPlayer);
    const { enabled: commentsEnabled } = useCommentsContext();
    const { isShowBlockQuestions: questionsEnabled } = useQuestionsContext();
    const { enabled: pollsEnabled } = usePollsContext();
    const { widgetSettings } = useWidgetContext();

    const [isPlayerStarted, setIsPlayerStarted] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const streamId = params.get('stream');
    const episodeId = params.get('episode');

    const isWidgetBottomSidebar = widgetSettings && widgetSettings.sidebarPosition === WIDGET_SIDEBAR_POSITION_BOTTOM;

    useEffect(() => {
        if (currentStream && (currentStream.id !== stream?.id || currentStream.status !== stream.status)) {
            const firstEpisode = currentStream.episode.filter(({ delay }) => !delay).sort((a, b) => a.timeStart - b.timeStart)[0];
            setStream(
                currentStream,
                currentStream.episode.every((episode) => getEpisodeRecordStatus(episode) === RECORD_STATUSES.EPISODES_CUT) ? firstEpisode : null,
            );
        }
    }, [currentStream]);

    useEffect(() => {
        if (!streamId) {
            return;
        }

        const stream = streams.find(({ id }) => String(id) === streamId);
        if (!stream) {
            return;
        }

        changeStream(stream);

        if (episodeId) {
            const episode = stream.episode.find(({ id }) => String(id) === episodeId);
            if (episode) {
                setStream(stream, episode);
                document.querySelector('#player-block')?.scrollIntoView({ block: 'center' });
            }
        }

        navigate(`${location.pathname}`);
    }, [streamId, episodeId, streams]);

    useEffect(() => {
        unmountPlayerRef.current = unmountPlayer;
    }, [unmountPlayer]);

    useEffect(() => {
        setEvent(event);
    }, [event]);

    useEffect(() => {
        if (!paused) {
            setIsPlayerStarted(true);
        }
    }, [paused]);

    useLayoutEffect(() => {
        mountPlayer();

        return () => {
            if (!isNotMountMiniPlayer?.current) {
                mountPlayer("miniplayer");
            } else {
                unmountPlayerRef.current && unmountPlayerRef.current();
                isNotMountMiniPlayer.current = false;
            }
        }
    }, []);

    const getStreamStatus = () => {
        if (stream) {

            const { isLive, isWaiting } = getStreamMeta(stream);

            if (stream.status === 'pause') {
                return STREAM_STATUSES.PAUSED;
            } else if (isFloating) {
                return STREAM_STATUSES.FLOATING;
            } else {
                if (isWaiting) {
                    return STREAM_STATUSES.WAITING;
                } else if (!isLive && stream.status === 'stop' && !isPlayerStarted) {
                    return STREAM_STATUSES.INTERRUPTED;
                }
            }
        } else {
            return STREAM_STATUSES.STOPPED;
        }
    };

    const streamStatus = getStreamStatus();

    const isSideModuleWidget = tabletView && isPlayerWidget || isWidgetBottomSidebar;

    const moduleMobileComponent = isSideModuleWidget ? ModuleMobileWidget : undefined;
    const isTabletViewFixed = isSideModuleWidget ? false : undefined;
    const isShowSideModule = ((isShowSideModuleByDefault && !fullScreen) || fullScreen === FULLSCREEN_MODES.CHAT);

    const className = `page-block broadcast-block ${!!fullScreen ? 'full-screen' : ''} ${!!tabletView || (isSideModuleWidget && !fullScreen) ? 'tabletView' : ''}
        ${!!fullScreen ? 'full-screen-' + fullScreen : ''} ${(isSideModuleWidget && !fullScreen) ? '-column' : ''}
        ${!isShowSideModule || tabletView || (!commentsEnabled && !questionsEnabled && !pollsEnabled) || (isSideModuleWidget && !fullScreen) ? "-full-width" : ''}
        ${isSideModuleWidget ? 'side-module-widget' : ''}`;

    const posterUrl = event.moduleTheme.poster
        || stream?.preview
        || streams[0]?.preview
        || '/player/images/preview.svg';

    return (
        <>
            <section
                className={className}
                ref={(e) => {
                    if (e) {
                        containerRef.current = e;
                    }
                }}
            >
                <section className={"player-block"} id="player-block" >
                    <div className="player-block-poster-container">
                        <div className="player-block-poster-image-container">
                            {!fullScreen && <img alt="" src={posterUrl} />}
                        </div>
                    </div>

                    <section id="player-block-container" className={(!streamStatus || streamStatus === STREAM_STATUSES.INTERRUPTED) ? 'player-block-full-height' : ''} />
                    {streamStatus &&
                        <div className="player-block-overlay-container">
                            {overlays[streamStatus](event, stream, onHidePlayer)}
                        </div>
                    }
                </section>
                {isShowSideModule && (
                    <SideModule
                        isOpenModule={settings?.isMobileOpenModule}
                        settings={settings}
                        ModuleMobileComponent={moduleMobileComponent}
                        isTabletViewFixed={isTabletViewFixed}
                        isWidgetBottomSidebar={isWidgetBottomSidebar}
                    />
                )}
            </section>
            <ButtonQuestionBlock settings={settings} />
        </>
    );
}
